/**
 * Characters: everybody there is, and how a new one is made.
 *
 * The same two screens the items panel has, because it is the same job. What
 * differs is the questions: somebody who does not fight is a name, a look, a
 * category and a few lines to say, and somebody who does is all of that plus
 * everything a fight reads from.
 *
 * The two kinds are separate tabs rather than a choice inside one form, because
 * making a shopkeeper and making a goblin are different pieces of work and the
 * list of each is what somebody wants to see while doing it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Wanted } from './find';
import type { AdminCommand } from '../Admin';
import { MODEL_NAMES } from '../../game/avatars';
import { Explain } from '../Explain';
import { ActsEditor } from './Acts';
import type { ItemDefinitionWire, ObjectDefinitionWire, QuestWire } from '@inverse/protocol';
import {
  blankCharacter,
  BONUS_FIELDS,
  characterFrom,
  COMBAT_SKILLS,
  draftFrom,
  ELEMENTS,
  problemWith,
  RECIPE_FREE_CATEGORIES,
  type CharacterDetail,
  type CharacterDraft,
  type CharacterKind,
  type ListedCharacter,
} from './characters';
import { SoundPicker } from './Sounds';

export function EditorCharacters({
  kind,
  list,
  detail,
  saves,
  wanted,
  items,
  quests,
  characters,
  objects,
  shops,
  onTalk,
  onCommand,
}: {
  /** Which of the two this panel is showing. */
  kind: CharacterKind;
  list: readonly ListedCharacter[];
  detail: CharacterDetail | null;
  /** How many characters have been kept this session, so a form knows it is done. */
  saves: number;
  /** A character somewhere else asked for, such as the search across everything. */
  wanted: Wanted | null;
  /** Everything the conditions and effects on an act can name. */
  items: readonly ItemDefinitionWire[];
  quests: readonly QuestWire[];
  characters: readonly { id: number; name: string }[];
  objects: readonly ObjectDefinitionWire[];
  shops: readonly { id: number; name: string }[];
  /** Opens this one's conversation on the canvas. */
  onTalk: (npcId: number) => void;
  onCommand: AdminCommand;
}): JSX.Element {
  const [chosen, setChosen] = useState<number | null>(null);
  const [draft, setDraft] = useState<CharacterDraft | null>(null);
  const [search, setSearch] = useState('');
  const [saying, setSaying] = useState('');
  const [seenSaves, setSeenSaves] = useState(saves);
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    onCommand('listCharacters');
  }, [onCommand]);

  if (seenSaves !== saves) {
    setSeenSaves(saves);
    setDraft(null);
    setSaying('');
    if (detail) setChosen(detail.character.id);
  }

  // Asked for from outside. Taken once, so pressing another row afterwards is
  // not undone by the request that opened the panel.
  const [took, setTook] = useState(0);
  if (wanted !== null && wanted.at !== took) {
    setTook(wanted.at);
    setChosen(Number(wanted.id));
    setDraft(null);
    onCommand('characterDetails', { npc: Number(wanted.id) });
  }

  const groups = useMemo(() => {
    const wanted = search.trim().toLowerCase();
    const mine = list.filter((entry) => entry.kind === kind);
    const matching = wanted
      ? mine.filter(
          (entry) => entry.name.toLowerCase().includes(wanted) || String(entry.id) === wanted,
        )
      : mine;

    const categories = [...new Set(matching.map((entry) => entry.category))].sort();
    return categories.map((category) => ({
      category,
      entries: matching.filter((entry) => entry.category === category),
    }));
  }, [kind, list, search]);

  const held = detail && detail.character.id === chosen ? detail : null;

  const save = (): void => {
    if (!draft) return;

    const problem = problemWith(draft);
    if (problem) {
      setSaying(problem);
      return;
    }

    setSaying('');
    onCommand(
      'saveNpc',
      {},
      { json: JSON.stringify(characterFrom(draft, held?.character ?? null)) },
    );
  };

  if (draft) {
    return (
      <CharacterForm
        draft={draft}
        saying={saying}
        items={items}
        quests={quests}
        characters={characters}
        objects={objects}
        shops={shops}
        onChange={setDraft}
        onSave={save}
        onCancel={() => {
          setDraft(null);
          setSaying('');
        }}
      />
    );
  }

  return (
    <>
      <button
        type="button"
        className="wide primary"
        onClick={() => {
          setDraft(blankCharacter(kind));
          setChosen(null);
        }}
      >
        {kind === 'enemy' ? 'New enemy' : 'New character'}
      </button>

      <label className="editor-field">
        <span>Search</span>
        <input
          type="text"
          value={search}
          placeholder={kind === 'enemy' ? 'goblin, rat' : 'keeper, smith'}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>

      {list.length === 0 ? <p className="hint">Asking the server who there is.</p> : null}

      <div className="editor-list">
        {groups.map((group) => (
          <div className="editor-group" key={group.category}>
            <h4>
              {group.category}
              <span>{group.entries.length}</span>
            </h4>
            {group.entries.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={entry.id === chosen ? 'editor-row on' : 'editor-row'}
                onClick={() => {
                  setChosen(entry.id);
                  setDraft(null);
                  onCommand('characterDetails', { npc: entry.id });
                }}
              >
                <span>{entry.name}</span>
                <span className="editor-row-id">{entry.id}</span>
              </button>
            ))}
          </div>
        ))}
      </div>

      {held ? (
        <CharacterFacts
          held={held}
          onEdit={() => setDraft(draftFrom(held.character))}
          onTalk={() => onTalk(held.character.id)}
          onPlace={() => onCommand('placeSpawnHere', { npc: held.character.id })}
        />
      ) : null}
    </>
  );
}

/** Everything worth reading about a character, without a form in the way. */
function CharacterFacts({
  held,
  onEdit,
  onTalk,
  onPlace,
}: {
  held: CharacterDetail;
  onEdit: () => void;
  onTalk: () => void;
  onPlace: () => void;
}): JSX.Element {
  const character = held.character;
  const fighting = (character.combatLevel ?? 0) > 0;
  const models = character.models ?? [];

  return (
    <div className="editor-facts">
      <h3>{character.name}</h3>
      <p className="hint">{character.examine || 'Nothing to say about them.'}</p>

      <dl className="editor-pairs">
        <dt>Id</dt>
        <dd>{character.id}</dd>
        <dt>Sort</dt>
        <dd>{character.category ?? 'other'}</dd>
        <dt>Drawn as</dt>
        <dd>{models.length > 1 ? models.join(', ') : (character.model ?? 'placeholder')}</dd>
        <dt>Menu</dt>
        <dd>{(character.actions ?? []).join(', ') || 'nothing'}</dd>
        <dt>Roam Range</dt>
        <dd>{character.wanderRange ?? 0} tiles</dd>
        {fighting ? (
          <>
            <dt>Aggro Range</dt>
            <dd>
              level {character.combatLevel}
              {character.boss ? ', a boss' : ''}
            </dd>
            <dt>Health</dt>
            <dd>{character.hitpoints}</dd>
            <dt>Max Hit</dt>
            <dd>{character.maxHit}</dd>
            <dt>Attack Speed</dt>
            <dd>every {character.attackSpeed} ticks</dd>
            <dt>Reach</dt>
            <dd>
              {(character.reach ?? 1) > 1
                ? `${character.reach} tiles, ${character.throws || 'nothing seen'}`
                : 'the next tile'}
            </dd>
            <dt>Aggro Range</dt>
            <dd>
              {(character.aggressionRange ?? 0) > 0
                ? `anybody within ${character.aggressionRange} tiles`
                : 'nobody'}
            </dd>
            <dt>Respawn</dt>
            <dd>{character.respawnTicks} ticks</dd>
          </>
        ) : null}
      </dl>

      {(character.lines ?? []).length > 0 ? (
        <p className="hint">Says: {(character.lines ?? []).join(' / ')}</p>
      ) : null}

      {held.drops && (held.drops.entries ?? []).length > 0 ? (
        <p className="hint">{(held.drops.entries ?? []).length} things it may leave behind.</p>
      ) : null}

      <button type="button" className="wide primary" onClick={onEdit}>
        Edit
      </button>
      {/* What they say, on the canvas. Written against the kind of person rather
          than against one of them standing on a tile, so it can be written
          before any of them have been put down anywhere. */}
      <button type="button" className="wide" onClick={onTalk}>
        Conversation
      </button>
      <button type="button" className="wide" onClick={onPlace}>
        Put one here
      </button>
    </div>
  );
}

/** The form, asking whatever the kind of character calls for. */
function CharacterForm({
  draft,
  saying,
  items,
  quests,
  characters,
  objects,
  shops,
  onChange,
  onSave,
  onCancel,
}: {
  draft: CharacterDraft;
  saying: string;
  /** Everything the conditions and effects on an act can name. */
  items: readonly ItemDefinitionWire[];
  quests: readonly QuestWire[];
  characters: readonly { id: number; name: string }[];
  objects: readonly ObjectDefinitionWire[];
  shops: readonly { id: number; name: string }[];
  onChange: (draft: CharacterDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}): JSX.Element {
  const set = (changed: Partial<CharacterDraft>): void => onChange({ ...draft, ...changed });
  const fighting = draft.kind === 'enemy';

  const text = (
    label: string,
    value: string,
    onText: (value: string) => void,
    placeholder = '',
  ): JSX.Element => (
    <label className="editor-field">
      <span>{label}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onText(event.target.value)}
      />
    </label>
  );

  const digits = (label: string, value: string, onText: (value: string) => void): JSX.Element =>
    text(label, value, (held) => onText(held.replace(/[^0-9]/g, '')));

  return (
    <>
      <h3>
        {draft.id > 0
          ? `Editing ${draft.name || 'somebody'}`
          : fighting
            ? 'A new enemy'
            : 'A new character'}
      </h3>

      {text('Called', draft.name, (value) => set({ name: value }), 'Grovekeeper')}
      {text(
        'Examines as',
        draft.examine,
        (value) => set({ examine: value }),
        'Keeps an eye on the trees.',
      )}

      <label className="editor-field">
        <span>Category</span>
        <select value={draft.category} onChange={(event) => set({ category: event.target.value })}>
          {RECIPE_FREE_CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </label>

      {text('Menu', draft.actions, (value) => set({ actions: value }), 'Talk to, Trade')}

      {/* What each of those entries does. The same builder scenery uses,
          because an act is an act wherever it hangs. */}
      <ActsEditor
        acts={draft.acts}
        occasions
        menu={draft.actions
          .split(',')
          .map((one) => one.trim())
          .filter((one) => one.length > 0)}
        items={items}
        quests={quests}
        characters={characters}
        objects={objects}
        shops={shops}
        onChange={(acts) => set({ acts })}
      />

      <h4>Model</h4>
      <div className="editor-brushes">
        {MODEL_NAMES.map((name) => (
          <button
            key={name}
            type="button"
            className={draft.model === name ? 'editor-tab on' : 'editor-tab'}
            onClick={() => set({ model: name, variants: draft.variants.filter((v) => v !== name) })}
          >
            {name}
          </button>
        ))}
      </div>

      <h4>Attachments</h4>
      <div className="editor-brushes">
        {MODEL_NAMES.filter((name) => name !== draft.model).map((name) => (
          <button
            key={name}
            type="button"
            className={draft.variants.includes(name) ? 'editor-tab on' : 'editor-tab'}
            onClick={() =>
              set({
                variants: draft.variants.includes(name)
                  ? draft.variants.filter((entry) => entry !== name)
                  : [...draft.variants, name],
              })
            }
          >
            {name}
          </button>
        ))}
      </div>
      <p className="hint">
        {draft.variants.length > 0
          ? `${draft.variants.length + 1} variants: each one put down is drawn as one of them.`
          : 'One look for all of them until a variant is added.'}
      </p>

      <div className="editor-grid">
        {digits('Roam Range', draft.wanderRange, (value) => set({ wanderRange: value }))}
        {digits('Size', draft.size, (value) => set({ size: value }))}
        {digits('Respawn', draft.respawnTicks, (value) => set({ respawnTicks: value }))}
      </div>

      {fighting ? (
        <>
          <h4>Combat</h4>
          <div className="editor-grid">
            {digits('Level', draft.combatLevel, (value) => set({ combatLevel: value }))}
            {digits('Health', draft.hitpoints, (value) => set({ hitpoints: value }))}
            {digits('Max Hit', draft.maxHit, (value) => set({ maxHit: value }))}
            {digits('Attack Speed', draft.attackSpeed, (value) => set({ attackSpeed: value }))}
            {digits('Aggro Range', draft.aggressionRange, (value) =>
              set({ aggressionRange: value }),
            )}
            {/* One is the next tile, which is a claw or a blade. More is
                somebody who shoots or casts: they stop where they are and work
                from there rather than closing in. */}
            {digits('Reach', draft.reach, (value) => set({ reach: value }))}
          </div>

          <label className="editor-field">
            <span>
              Throws
              <Explain>
                What is seen crossing the ground when it strikes from a distance: an arrow, a bolt,
                or air, water, earth or fire. Empty draws nothing, which is right for a thing that
                lashes out with a long arm.
              </Explain>
            </span>
            <select value={draft.throws} onChange={(event) => set({ throws: event.target.value })}>
              <option value="">Nothing</option>
              <option value="arrow">An arrow</option>
              <option value="bolt">A bolt</option>
              <option value="air">Air</option>
              <option value="water">Water</option>
              <option value="earth">Earth</option>
              <option value="fire">Fire</option>
            </select>
          </label>

          {/* What magic it suffers from, and what barely touches it. This is
              what makes the four ways of casting a decision rather than four
              names for the same spell. */}
          <h4>Resistances</h4>
          <div className="editor-grid">
            <label className="editor-small">
              <span>Weak To</span>
              <select
                value={draft.weakTo}
                onChange={(event) => set({ weakTo: event.target.value })}
              >
                <option value="">nothing in particular</option>
                {ELEMENTS.map((element) => (
                  <option key={element} value={element}>
                    {element}
                  </option>
                ))}
              </select>
            </label>

            <label className="editor-small">
              <span>Resists</span>
              <select
                value={draft.strongTo}
                onChange={(event) => set({ strongTo: event.target.value })}
              >
                <option value="">nothing in particular</option>
                {ELEMENTS.map((element) => (
                  <option key={element} value={element}>
                    {element}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="editor-brushes">
            <button
              type="button"
              className={draft.boss ? 'editor-tab on' : 'editor-tab'}
              onClick={() => set({ boss: !draft.boss })}
            >
              Worth a leaderboard
            </button>
          </div>

          <h4>Sounds</h4>
          <SoundPicker
            label="Attacking"
            value={draft.sounds.attack}
            empty="The usual swing"
            onChange={(attack) => set({ sounds: { ...draft.sounds, attack } })}
          />
          <SoundPicker
            label="Hurt"
            value={draft.sounds.hurt}
            empty="The usual blow"
            onChange={(hurt) => set({ sounds: { ...draft.sounds, hurt } })}
          />
          <SoundPicker
            label="Dying"
            value={draft.sounds.die}
            empty="The usual death"
            onChange={(die) => set({ sounds: { ...draft.sounds, die } })}
          />

          <h4>Levels</h4>
          <div className="editor-grid">
            {COMBAT_SKILLS.map((skill) => (
              <label className="editor-small" key={skill}>
                <span>{skill}</span>
                <input
                  type="text"
                  value={draft.levels[skill] ?? ''}
                  onChange={(event) =>
                    set({
                      levels: {
                        ...draft.levels,
                        [skill]: event.target.value.replace(/[^0-9]/g, ''),
                      },
                    })
                  }
                />
              </label>
            ))}
          </div>

          <h4>Attack</h4>
          <div className="editor-grid">
            {BONUS_FIELDS.slice(0, 5).map(([field, label]) => (
              <Bonus key={field} field={field} label={label} draft={draft} onChange={onChange} />
            ))}
          </div>

          <h4>Defence</h4>
          <div className="editor-grid">
            {BONUS_FIELDS.slice(5).map(([field, label]) => (
              <Bonus key={field} field={field} label={label} draft={draft} onChange={onChange} />
            ))}
          </div>
        </>
      ) : (
        <>
          <h4>Dialogue</h4>
          {draft.lines.map((line, at) => (
            <div className="editor-condition" key={at}>
              <span className="editor-count-quiet">{at + 1}</span>
              <input
                type="text"
                value={line}
                onChange={(event) =>
                  set({
                    lines: draft.lines.map((held, index) =>
                      index === at ? event.target.value.slice(0, 160) : held,
                    ),
                  })
                }
              />
              <button
                type="button"
                className="editor-tab"
                title="Delete this line"
                onClick={() => set({ lines: draft.lines.filter((_, index) => index !== at) })}
              >
                ✕
              </button>
            </div>
          ))}

          <button
            type="button"
            className="wide"
            onClick={() => set({ lines: [...draft.lines, 'And another thing.'] })}
          >
            Add a line
          </button>

          <p className="hint">
            Plain lines, said in order.
            <Explain>
              A conversation that answers back is written in the Conversation panel, against whoever
              is selected in the world.
            </Explain>
          </p>
        </>
      )}

      {saying ? <p className="hint warning">{saying}</p> : null}

      <button type="button" className="wide primary" onClick={onSave}>
        {draft.id > 0 ? 'Save the changes' : 'Make them'}
      </button>
      <button type="button" className="wide" onClick={onCancel}>
        Back to the list
      </button>
    </>
  );
}

/** One bonus box, of which there are ten and all the same. */
function Bonus({
  field,
  label,
  draft,
  onChange,
}: {
  field: string;
  label: string;
  draft: CharacterDraft;
  onChange: (draft: CharacterDraft) => void;
}): JSX.Element {
  return (
    <label className="editor-small">
      <span>{label}</span>
      <input
        type="text"
        value={draft.bonuses[field] ?? '0'}
        onChange={(event) =>
          onChange({
            ...draft,
            bonuses: { ...draft.bonuses, [field]: event.target.value.replace(/[^0-9-]/g, '') },
          })
        }
      />
    </label>
  );
}
